import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdGateGraphLayout, HdGateGraphNode } from './hd-gate-graph.models';
import { edgeSubpath } from './hd-gate-graph-scene';

/** Alleen deze twee zijden tellen; `'highlight'` is een editor-hulpstaat. */
export type HdGateGraphSide = 'personality' | 'design';

export const HD_GATE_GRAPH_SIDE_COLORS: Readonly<Record<HdGateGraphSide, string>> = {
  personality: '#0e0e0e',
  design: '#c0392b',
};

/** Eén opgelichte connector. */
export interface HdGateGraphHighlightEdgeVm {
  readonly id: string;
  readonly d: string;
  readonly color: string;
  readonly width: number;
  /** Iets bredere witte stroke eronder, zodat het pad traceerbaar blijft. */
  readonly casingWidth: number;
}

/**
 * Eén opgelichte cirkel. Wanneer beide zijden dezelfde knoop raken wordt hij
 * half-om-half getekend: links personality, rechts design.
 */
export interface HdGateGraphHighlightNodeVm {
  readonly id: string;
  readonly layer: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  /** Witte halo eronder. */
  readonly haloRadius: number;
  readonly split: boolean;
  /** Volledige cirkel/ring wanneer `split` false is. */
  readonly color: string;
  /**
   * Linkerhelft (personality) en rechterhelft (design) bij `split` — open
   * bogen, want de zijde-kleur is uitsluitend een RAND. De inhoud van een
   * opgelichte cirkel houdt haar eigen laagkleur en tekst.
   */
  readonly leftPath: string;
  readonly rightPath: string;
  readonly strokeWidth: number;
}

export interface HdGateGraphHighlight {
  readonly edges: readonly HdGateGraphHighlightEdgeVm[];
  readonly nodes: readonly HdGateGraphHighlightNodeVm[];
  /** Zijden per knoop-id — handig om te testen zonder door de VMs te lopen. */
  readonly sidesById: ReadonlyMap<string, ReadonlySet<HdGateGraphSide>>;
}

const EMPTY: HdGateGraphHighlight = { edges: [], nodes: [], sidesById: new Map() };

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * Halve boog van boven naar onder; `sweep` 0 = linkerhelft, 1 = rechterhelft.
 * Open boog: de zijde-kleur is enkel een rand, dus de diameter mag niet mee
 * getekend worden.
 */
function halfArc(node: HdGateGraphNode, radius: number, sweep: 0 | 1): string {
  const r = round(radius);
  const top = `${round(node.x)} ${round(node.y - radius)}`;
  const bottom = `${round(node.x)} ${round(node.y + radius)}`;
  return `M${top}A${r} ${r} 0 0 ${sweep} ${bottom}`;
}

/**
 * Zoekt de knoop die een activatie exact aanwijst. Elke planeet-activatie draagt
 * `line`, `color`, `tone` én `base` en wijst daarmee één van de 1080 eindpunten
 * aan; het pad ernaartoe is de keten die oplicht.
 *
 * Geeft een lege keten bij waarden buiten bereik (lijn/kleur/toon 1..6,
 * basis 1..5), zodat rommelige data de tekening niet stukmaakt.
 */
function resolveChain(
  layout: HdGateGraphLayout,
  activation: HdBodygraphPlanetaryActivation,
): readonly HdGateGraphNode[] {
  const steps = [activation.line, activation.color, activation.tone, activation.base];
  const chain: HdGateGraphNode[] = [layout.root];
  let current: HdGateGraphNode = layout.root;
  for (const step of steps) {
    if (!Number.isInteger(step) || step < 1 || step > current.children.length) return [];
    current = current.children[step - 1];
    chain.push(current);
  }
  return chain;
}

/**
 * Bouwt de opgelichte paden voor de activaties van deze poort.
 *
 * Alles wat niet in een pad zit wordt door de component gedimd; deze functie
 * levert enkel wat oplicht. Zie de spec §7.3.
 */
export function buildHdGateGraphHighlight(
  layout: HdGateGraphLayout,
  activations: readonly HdBodygraphPlanetaryActivation[],
): HdGateGraphHighlight {
  if (activations.length === 0) return EMPTY;

  const sidesByNode = new Map<HdGateGraphNode, Set<HdGateGraphSide>>();
  const edgesByKey = new Map<string, { from: HdGateGraphNode; to: HdGateGraphNode; side: HdGateGraphSide }>();

  for (const activation of activations) {
    if (activation.side !== 'personality' && activation.side !== 'design') continue;
    const side = activation.side;
    const chain = resolveChain(layout, activation);
    if (chain.length === 0) continue;

    for (const node of chain) {
      const sides = sidesByNode.get(node) ?? new Set<HdGateGraphSide>();
      sides.add(side);
      sidesByNode.set(node, sides);
    }
    for (let i = 0; i < chain.length - 1; i++) {
      edgesByKey.set(`${chain[i + 1].id}:${side}`, { from: chain[i], to: chain[i + 1], side });
    }
  }

  if (sidesByNode.size === 0) return EMPTY;

  const edgeWidths = [0, 1.3, 0.95, 0.6, 0.4];
  const edges = [...edgesByKey.entries()].map(([key, edge]) => ({
    id: key,
    d: edgeSubpath(edge.from, edge.to),
    color: HD_GATE_GRAPH_SIDE_COLORS[edge.side],
    width: edgeWidths[edge.to.layer],
    casingWidth: edgeWidths[edge.to.layer] + 1.1,
  }));

  const nodes = [...sidesByNode.entries()].map(([node, sides]) => ({
    id: node.id,
    layer: node.layer,
    x: round(node.x),
    y: round(node.y),
    radius: round(node.radius),
    haloRadius: round(node.radius + 1.1),
    split: sides.size > 1,
    color: HD_GATE_GRAPH_SIDE_COLORS[[...sides][0]],
    leftPath: halfArc(node, node.radius, 0),
    rightPath: halfArc(node, node.radius, 1),
    // Dunner naarmate de cirkel kleiner is; op kleur, toon en basis zou een
    // dikke rand het bolletje opslokken.
    strokeWidth: node.layer === 0 ? 1.8 : node.layer === 1 ? 1.6 : node.layer === 2 ? 0.55 : 0.35,
  }));

  const sidesById = new Map<string, ReadonlySet<HdGateGraphSide>>();
  for (const [node, sides] of sidesByNode) sidesById.set(node.id, sides);

  return { edges, nodes, sidesById };
}
